<script setup lang="ts">
import { computed } from 'vue'
import { formatMoney } from '@/utils/formatMoney'
import { defaultPrecision } from '@/config/money'

/**
 * Bal. column cell for the client card visits table. Renders the running
 * balance (in minor currency units) computed by `useAlignedClientStreams`
 * and tints the value by tier — same thresholds as the mock:
 *
 * - balance &lt; 0                        ⇒ `neg` (negative-money red).
 * - balance &lt; `lowBalanceThresholdMajor` ⇒ `low` (warning gold).
 * - balance ≥ `lowBalanceThresholdMajor` ⇒ `pos` (positive-money green).
 *
 * When the row precedes any monthly snapshot, `balanceMinor` is undefined
 * and the cell falls back to the muted placeholder; this never happens once
 * the alignment loop has caught the balances stream up.
 */
const props = withDefaults(
  defineProps<{
    /** Running balance in minor currency units, or undefined when the row precedes any snapshot. */
    balanceMinor: number | undefined
    /**
     * Balance threshold in major currency units. Balances below this value
     * are considered low and rendered in warning gold (`--color-visit-stale`).
     * Defaults to `50`, matching the mock's low-credit highlight level.
     */
    lowBalanceThresholdMajor?: number
    /** Balance did not move (a subscription covered the lesson): renders muted instead of tiered. */
    flat?: boolean
    /** Placeholder shown when `balanceMinor` is missing. Defaults to `–`. */
    emptyPlaceholder?: string
    /** Forwarded from `DynamicTable` column config; caps cell width when set. */
    maxWidth?: string
  }>(),
  {
    lowBalanceThresholdMajor: 50,
    flat: false,
    emptyPlaceholder: '–',
    maxWidth: undefined,
  },
)

const hasValue = computed(
  () => typeof props.balanceMinor === 'number' && Number.isFinite(props.balanceMinor),
)

const tier = computed<'neg' | 'low' | 'pos'>(() => {
  if (!hasValue.value) return 'pos'
  const major = props.balanceMinor! / 10 ** defaultPrecision
  if (major < 0) return 'neg'
  if (major < props.lowBalanceThresholdMajor) return 'low'
  return 'pos'
})

const formatted = computed(() => {
  if (!hasValue.value) return null
  const out = formatMoney({ amount: props.balanceMinor! }).formatted
  /* Positive balances get an explicit `+` prefix to match the mock; negatives carry their own minus. */
  if (props.balanceMinor! > 0) return `+${out}`
  return out
})
</script>

<template>
  <td
    class="running-balance-cell font-tufte-mono"
    :class="props.flat ? 'running-balance-cell--flat' : `running-balance-cell--${tier}`"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <span v-if="hasValue">{{ formatted }}</span>
    <span v-else class="text-tufte-muted">{{ emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.running-balance-cell {
  font-family: var(--font-tufte-mono);
  text-align: right;
  /* Inherit `font-size` from the surrounding table so the running balance
   * matches the row density (the client card sets the table to
   * `--text-tufte-xs`). Mirrors the same change in `SignedAmountCell`
   * after the user reported that the previous `--text-tufte-sm` made the
   * last two columns visually larger than the date / type cells. */
}

.running-balance-cell--neg {
  color: var(--color-money-negative);
}

.running-balance-cell--low {
  /* `--color-visit-stale` is the warning gold used across the app (visit
   * freshness, low-credit balance); reusing it keeps the low-balance signal
   * visually consistent with other caution indicators. */
  color: var(--color-visit-stale);
}

.running-balance-cell--flat {
  color: var(--color-tufte-muted);
}

.running-balance-cell--pos {
  color: var(--color-money-positive);
}
</style>
